import { View } from '@/components/ui/view';
import { Text } from '@/components/ui/text';
import { ChevronDownIcon } from '@/components/ui/icon';
import { Select, SelectTrigger, SelectInput, SelectIcon, SelectPortal, SelectContent, SelectItem } from '@/components/ui/select';
import { HStack } from '@/components/ui/hstack';
import { SafeAreaView } from 'react-native-safe-area-context';
import { GlobalState } from '@/containers/GlobalState';
import * as Print from 'expo-print';
import { shareAsync } from 'expo-sharing';
import QRCode from 'qrcode';
import React, { useState, useEffect, useContext } from 'react';
import { WebView } from 'react-native-webview';
import i18n from '@/assets/i18n';
import FloatingLabelInput from '@/components/basic/FloatingLabelInput';
import Footer from '@/components/basic/Footer';
import ModalPageSetup from '@/screens/modals/ModalPageSetup';
import { captureScreen } from '@/components/selective';
import { manipulateAsync } from 'expo-image-manipulator';
import { Platform } from 'react-native';

const sizes = { pm: 4.76, w: 595, h: 842, x: 1000, y: 1408, p: 'A4', wM: 210, hM: 297 }; // Din A4 72 dpi
const mm2px = (m) => {
    return Math.ceil(10 * m * sizes.pm) / 10;
};
export default function PrintQRCode({ route, navigation }) {
    const { codes, single } = route.params;

    // p: Page Margin, l: label, q: qrcode, g: l=Label, f=Frame, i=InA, d=Debug, p=Pattern, c=Columns, r=Rows, b=Start, n=Count
    const formats = single
        ? [{ idx: 0, p: { ml: 7, mt: 13 }, l: { id: 'Single', w: 40, dw: 0, h: 80, dh: 0, dist: 8, font: '18' }, q: { s: 30, dist: 5 }, g: { l: true, f: false, i: true, d: false, p: false, b: 1, n: 1 } }]
        : [
              { idx: 0, p: { ml: 7, mt: 13 }, l: { id: 'L4773', w: 63.5, dw: 2.0, h: 33.9, dh: 0, dist: 8, font: '16' }, q: { s: 23, dist: 5 }, g: { l: true, f: false, i: false, d: false, p: false, b: 1, n: 4 } },
              { idx: 1, p: { ml: 10, mt: 22 }, l: { id: 'L4736', w: 45.7, dw: 2.0, h: 21.2, dh: 0, dist: 3, font: '11' }, q: { s: 16, dist: 2 }, g: { l: true, f: false, i: true, d: false, p: false, b: 1, n: 4 } }
          ];

    const [selectedPrinter, setSelectedPrinter] = useState();
    const [html, setHTML] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [format, setFormat] = useState(formats[0]);
    const { inAColors } = useContext(GlobalState);

    const printPDF = async () => {
        // On iOS/android prints the given html. On web prints the HTML from the current page.
        const result = await Print.printAsync({ html: getPage(true), width: sizes.w, height: sizes.h, printerUrl: selectedPrinter?.url });
        console.log('PRINT', result);
    };
    const sendPDF = async () => {
        // On iOS/android prints the given html. On web prints the HTML from the current page.
        const { uri } = await Print.printToFileAsync({ html: getPage(true), width: sizes.w, height: sizes.h }); // Android A4: 20.99 x 29,64 with 72dpi but 2 pages, iOS Letter: 21,59 x 27,94 with 72dpi
        console.log('File has been saved to:', uri);
        await shareAsync(uri, { UTI: '.pdf', mimeType: 'application/pdf' });
    };

    const sendJPG = async () => {
        captureScreen({
            format: 'jpg',
            quality: 0.8
        }).then(
            async (uri) => {
                console.ina('Screenshot saved to', uri);
                const result0 = await manipulateAsync(
                    uri,
                    [
                        {
                            resize: {
                                height: 1024
                            }
                        }
                    ],
                    {}
                );
                const result = await manipulateAsync(
                    result0.uri,
                    [
                        {
                            crop: {
                                originX: 0,
                                originY: 0,
                                width: result0.width,
                                height: 800
                            }
                        }
                    ],
                    {}
                );
                console.log('RESULT1', result);
                await shareAsync(result.uri, { UTI: '.jpg', mimeType: 'image/jpeg' });
            },
            (error) => {
                console.error('Screenshot failed: ', error);
            }
        );
    };

    const send = () => {
        if (single) {
            sendJPG();
        } else {
            sendPDF();
        }
    };

    const actions = [
        { icon: 'keyboard-arrow-left', text: 'footer.back', component: 'BACK' },
        { icon: 'print', text: 'printQRCode.print', exec: printPDF },
        { icon: 'send', text: 'printQRCode.share', exec: send }
    ];

    const splitTextByLength = (text, maxLength) => {
        const words = text.split(/\s+/);
        const chunks = [];
        let currentChunk = '';

        for (let i = 0; i < words.length; i++) {
            const word = words[i];
            const separator = currentChunk ? ' ' : '';

            // Wenn das Wort reinpasst → einfach anhängen
            if ((currentChunk + separator + word).length <= maxLength) {
                currentChunk += separator + word;
                continue;
            }

            // Falls das Wort zu lang ist → in Teile schneiden
            if (word.length > maxLength) {
                const parts = word.match(new RegExp(`.{1,${maxLength}}`, 'g'));

                // Alle vollen Teile sofort hinzufügen
                for (let j = 0; j < parts.length - 1; j++) {
                    chunks.push(parts[j]);
                }

                // Das letzte Teil behalten — es wird mit dem nächsten Wort aufgefüllt
                currentChunk = parts[parts.length - 1];
            } else {
                // Wenn aktueller Chunk existiert → abschließen
                if (currentChunk) chunks.push(currentChunk);
                // Neues Chunk mit aktuellem Wort beginnen
                currentChunk = word;
            }
        }

        if (currentChunk) chunks.push(currentChunk);
        return chunks;
    };

    const generateSVGs = () => {
        const promises = codes.map((code) => {
            return new Promise((resolve, reject) => {
                QRCode.toString(code.data, { type: 'svg', errorCorrectionLevel: 'Q', margin: 0 }, function (error, svg) {
                    if (svg) {
                        code.info = svg.match(/"[^"]+"/g).map((i) => i.replace(/"/g, ''));
                        resolve(code);
                    } else {
                        console.log('SVG!', error, code);
                        reject(error);
                    }
                });
            });
        });
        Promise.all(promises).then((results) => {
            setHTML(getPage(false));
        });
    };

    const getPage = (print) => {
        const page = getFullPage(print);
        // console.log(page)
        return page;
    };

    const showSetup = () => {
        console.log('XXX', format);
        setShowModal(true);
    };

    useEffect(() => {
        generateSVGs();
    }, []);

    useEffect(() => {
        setHTML(getPage(false));
    }, [format]);

    useEffect(() => {
        if (!showModal) {
            setHTML(getPage(false));
        }
    }, [showModal]);

    return (
        <View className="flex-1 bg-secondary-50">
            <SafeAreaView edges={['top']}>
                <Text className="text-lg text-secondary-900 self-center">{i18n.t('printQRCode.title')}</Text>
                {!single && (
                    <HStack space="xs" className={` self-center mt-5 `}>
                        <Select accessibilityLabel={i18n.t('scheduleWorkflow.repeat')} onValueChange={(itemValue) => setFormat(formats[itemValue])} className="w-[110px]">
                            <SelectTrigger variant="outline" size="md" className="border-secondary-900">
                                <SelectInput placeholder={formats[format.idx].l.id} value={format.idx} />
                                <SelectIcon className="absolute right-3 text-secondary-900" as={ChevronDownIcon} />
                            </SelectTrigger>
                            <SelectPortal>
                                <SelectContent>
                                    <SelectItem label={formats[0].l.id} value={0} />
                                    <SelectItem label={formats[1].l.id} value={1} />
                                </SelectContent>
                            </SelectPortal>
                        </Select>
                        <FloatingLabelInput label={i18n.t('printQRCode.start')} w="w-[70]" keyboardType="numbers-and-punctuation" textAlign="right" autoCorrect={false} value={'' + format.g.b} onChangeText={(num) => setFormat({ ...format, g: { ...format.g, b: num } })} />
                        <FloatingLabelInput label={i18n.t('printQRCode.count')} w="w-[70]" keyboardType="numbers-and-punctuation" textAlign="right" autoCorrect={false} value={'' + format.g.n} onChangeText={(num) => setFormat({ ...format, g: { ...format.g, n: num } })} />
                    </HStack>
                )}
            </SafeAreaView>
            <WebView width="100%" source={{ html }} />
            <ModalPageSetup showModal={showModal} setShowModal={setShowModal} format={format} setFormat={setFormat} single={single} />
            <Footer navigation={navigation} actions={actions} button={{ action: showSetup, info: 'printQRCode.setup' }} />
        </View>
    );

    function getFullPage(print) {
        let idx = 0;
        let info = format.g.d ? `<Text text-anchor="middle" x="50%" y="40" font-size="20" fill="#000000" stroke="none">${Platform.OS + ', ' + format.l.id + ', pm:' + sizes.pm + ', svg:' + sizes.x + 'x' + sizes.y + ', px:' + sizes.w + 'x' + sizes.h + ', ' + sizes.p + ', ' + new Date().toLocaleString()}</text>` : '';
        let label = format.g.l ? (single ? getPrintLabel(codes[0], print) : getPrintLabel(codes[idx], true)) : '';
        let content = single && !print ? '' : (format.g.d ? getPattern() : '') + info + `  <g transform="translate(${mm2px(format.p.ml)} ${mm2px(format.p.mt)})">`;
        let cnt = single ? format.g.n - 1 : -format.g.b + 1;
        let cols = Math.floor(sizes.wM / (format.l.w + 2 * format.l.dw));
        let rows = Math.floor(sizes.hM / (format.l.h + 2 * format.l.dh));
        for (let r = 0; r < rows; r++) {
            for (let c = 0; c < cols; c++) {
                if (cnt++ > -1) {
                    if (cnt <= format.g.n) {
                        content += `
    <g transform="translate(${c * mm2px(format.l.w + format.l.dw)} ${r * mm2px(format.l.h + format.l.dh)})">
      ${label}
    </g>`;
                    } else {
                        if (++idx < codes.length) {
                            label = format.g.l ? getPrintLabel(codes[idx], true) : '';
                            cnt = 1;
                            content += `
    <g transform="translate(${c * mm2px(format.l.w + format.l.dw)} ${r * mm2px(format.l.h + format.l.dh)})">
      ${label}
    </g>`;
                        }
                    }
                }
            }
        }
        content += single || !format.g.f ? '' : '\n  </g>';
        return `
<!DOCTYPE html>
<html>
<head>
<style>
body {
    margin: 0;
    padding: 0;
}
</style>
</head>
<body>
<svg viewBox="0 0 ${sizes.x} ${sizes.y}" font-size="20" font-family="Verdana, sans-serif" fill="none" stroke="#000000" stroke-width="1" shapeRendering="crispEdges" xmlns="http://www.w3.org/2000/svg">
${content}
</svg>  
</body>
</html>`;
    }

    function getPrintLabel(code, print) {
        let rotate = format.l.w > format.l.h;
        let lW = rotate ? format.l.h : format.l.w;
        let lH = rotate ? format.l.w : format.l.h;
        code.lines = splitTextByLength(code.text, Math.floor(mm2px(lW) / (format.l.font * 0.6)));
        const sCode = mm2px(format.q.s) / code.info[1].split(/ /)[3];
        let label = format.g.f ? `<rect width="${mm2px(lW)}" height="${mm2px(lH)}"/>` : '';
        let qrMarginTop = mm2px(format.q.dist);
        if (format.g.i) {
            label += `<g transform="translate(${(mm2px(lW) - 68) / 2} 16)">${getLogo()}</g>`;
            qrMarginTop += 68 + 16;
        }
        const textX = mm2px(lW / 2);
        label += `<g transform="translate(${mm2px(lW - format.q.s) / 2} ${qrMarginTop}) scale(${sCode})">
          <path stroke="FFFFFF" d="${code.info[6]}" />
        </g>
        <Text font-size="${format.l.font}" text-anchor="middle" x="${textX}" y="${qrMarginTop + mm2px(format.q.s + format.l.dist)}" fill="#000000" stroke="none">${code.lines.map((line) => `<tspan x="${textX}" dy="${format.l.font}">${line}</tspan>`).join('')}</text>`;
        const scale = 900 / mm2px(Math.max(lW, lH));
        if (rotate) {
            return `
      <g transform="translate(${print ? mm2px(lH) : 950} ${print ? 0 : mm2px(lH)}) rotate(90) scale(${print ? 1 : scale})">
        ${label}
      </g>`;
        } else {
            return `
      <g transform="translate(${print ? 0 : (sizes.x - mm2px(lW) * scale) / 2} 0) scale(${print ? 1 : scale})">
        ${label}
      </g>`;
        }
        return;
    }
    //   <g transform="translate(950, ${mm2px(lH)}) rotate(90) scale(${print ? 1 : scale})">

    function getLogo() {
        return `
<path fill="${inAColors.primary[900]}" stroke="none" d="M68,65.2c0,1.6-1.3,2.8-2.8,2.8H2.8C1.3,68,0,66.8,0,65.2V2.8C0,1.3,1.3,0,2.8,0h62.4C66.8,0,68,1.3,68,2.8 V65.2z"/>
<path fill="#FFFFFF" stroke="none" d="M39.1,5.2c0.7,0,1.3,0.4,1.7,0.9c0.6,0.8,1.3,2.7-0.1,3.2c-0.7,0.3-0.1,1.4-0.1,2.1
	c0.7-0.3,1.3-0.4,2-0.9c1.1-0.8,1.9-2.6,2-2.7c0.2-0.3,0.6-0.2,0.8,0c0.2,0.2,0.2,0.5,0.1,0.8c-0.8,2.1-1.3,3.2-4.1,4.3
	c-0.4,0.2-1,2.2-0.7,2.5c0.2,0.2,1.3-0.1,1.8-0.1c1.5,0.2,2.5,1.3,2.4,2.8c0,0.8-0.1,1.7-0.3,2.5h-2.4c0-0.5,0.1-1,0.1-1.5
	c0-0.6-0.2-0.8-0.7-0.9c-1.2-0.2-1.8,0.1-2.2,1.2c-1,2.6-2.1,5.1-3.4,7.6c-0.8-0.6-1.7-1-2.8-1.2c0.7-1.1,1.4-2.2,2.2-3.2
	c1.9-2.3,2.7-5,3.2-7.8c0.2-1,0-2.3,0.2-3.5c-0.9,0.7-1.1,1.8-1.6,2.7c-1.1,1.9-3.3,3.9-4.7,5.6c-0.3,0.3-0.6,0.5-1,0.3
	c-0.4-0.2-0.3-0.6-0.2-0.9c0-0.2,0.1-0.4,0.2-0.6c0.3-0.6,0.4-1.2,0.1-1.9c-0.3-0.8,0.3-1.7,0-2.6c-0.1-0.3,0.3-0.4,0.6-0.6
	c2-1.2,4.6-2.9,7.1-3.5c-1.9-0.4-2.7-1.2-2.7-2.5c0-1,0.7-1.8,1.7-2.1C38.7,5.2,38.9,5.2,39.1,5.2z M10.6,21.9h6.5v24.5h-6.5
	C10.6,46.4,10.6,21.9,10.6,21.9z M42.1,21.9h7.9l7.5,24.5H50l-1.2-5.2H43l-1.2,5.2h-7.2L42.1,21.9z M45.8,26.7l-2.1,9.5h4.5
	l-2.2-9.5H45.8z M31.8,27.1c0.3,0,0.6,0,0.9,0.1v0c0,0,0,0,0.1,0c0.2,0,0.5,0.1,0.7,0.1c0.1,0,0.1,0,0.2,0c0.2,0,0.4,0.1,0.6,0.2
	c0.1,0,0.1,0,0.2,0.1c0.2,0.1,0.3,0.2,0.5,0.2c0,0,0.1,0,0.1,0.1c0.5,0.3,1,0.8,1.4,1.3c0.6,0.9,1,2.1,1.2,3.5l-4.2,13.8h-2.1V35.6
	c0-1.2-0.2-2-0.5-2.4c-0.3-0.4-0.8-0.6-1.4-0.6c-0.6,0-1.1,0.1-1.5,0.4c-0.4,0.3-0.9,0.6-1.4,1.1v12.4H20V27.6h5.3l0.5,2.3h0.2
	c0.8-0.7,1.6-1.4,2.6-1.9C29.4,27.4,30.5,27.1,31.8,27.1z M50,50.1h1.8v3.4l-0.1,1.8c0.4-0.4,0.8-0.7,1.3-1c0.5-0.3,1-0.4,1.6-0.4
	c1,0,1.7,0.3,2.1,0.9c0.4,0.6,0.6,1.5,0.6,2.6V63h-1.8v-5.4c0-0.8-0.1-1.3-0.4-1.7c-0.2-0.3-0.6-0.5-1.2-0.5
	c-0.4,0-0.8,0.1-1.1,0.3c-0.3,0.2-0.7,0.5-1.1,0.9V63H50V50.1z M15,53.7c0.6,0,1.1,0.1,1.5,0.3c0.4,0.2,0.8,0.4,1.1,0.7l-0.9,1.2
	c-0.2-0.2-0.5-0.4-0.7-0.5c-0.3-0.1-0.5-0.2-0.9-0.2c-0.5,0-0.9,0.1-1.3,0.4c-0.4,0.3-0.7,0.6-0.9,1.1c-0.2,0.5-0.3,1-0.3,1.7
	c0,0.6,0.1,1.2,0.3,1.7c0.2,0.5,0.5,0.8,0.9,1.1c0.4,0.3,0.8,0.4,1.3,0.4c0.4,0,0.7-0.1,1.1-0.2c0.3-0.2,0.6-0.4,0.9-0.6l0.8,1.2
	c-0.4,0.4-0.8,0.6-1.4,0.8c-0.5,0.2-1,0.3-1.5,0.3c-0.8,0-1.5-0.2-2.2-0.6c-0.6-0.4-1.1-0.9-1.5-1.6c-0.4-0.7-0.6-1.5-0.6-2.5
	c0-1,0.2-1.8,0.6-2.5c0.4-0.7,0.9-1.2,1.6-1.6C13.5,53.8,14.2,53.7,15,53.7z M24.1,53.7c0.7,0,1.4,0.2,2.1,0.5
	c0.6,0.4,1.1,0.9,1.5,1.6c0.4,0.7,0.6,1.5,0.6,2.5c0,1-0.2,1.8-0.6,2.5c-0.4,0.7-0.9,1.2-1.5,1.6c-0.6,0.4-1.3,0.6-2.1,0.6
	c-0.7,0-1.4-0.2-2-0.6c-0.6-0.4-1.2-0.9-1.5-1.6c-0.4-0.7-0.6-1.5-0.6-2.5c0-1,0.2-1.8,0.6-2.5c0.4-0.7,0.9-1.2,1.5-1.6
	C22.7,53.8,23.3,53.7,24.1,53.7z M34.3,53.7c0.7,0,1.3,0.1,1.8,0.4c0.5,0.3,0.8,0.7,1.1,1.3c0.2,0.6,0.3,1.2,0.3,2v5.3H36l-0.1-1
	h-0.1c-0.4,0.3-0.8,0.6-1.3,0.9C34,62.9,33.5,63,33,63c-0.5,0-1-0.1-1.4-0.3c-0.4-0.2-0.7-0.5-0.9-0.9c-0.2-0.4-0.3-0.8-0.3-1.4
	c0-1,0.4-1.7,1.3-2.2c0.8-0.5,2.2-0.9,4-1.1c0-0.4-0.1-0.7-0.2-1c-0.1-0.3-0.3-0.5-0.5-0.7c-0.2-0.2-0.6-0.3-1-0.3
	c-0.5,0-0.9,0.1-1.4,0.3c-0.4,0.2-0.8,0.4-1.2,0.6l-0.7-1.2c0.3-0.2,0.6-0.4,1-0.6c0.4-0.2,0.8-0.3,1.2-0.4
	C33.4,53.7,33.8,53.7,34.3,53.7z M44.7,53.7c0.6,0,1.1,0.1,1.5,0.3c0.4,0.2,0.8,0.4,1.1,0.7l-0.9,1.2c-0.2-0.2-0.5-0.4-0.7-0.5
	s-0.5-0.2-0.9-0.2c-0.5,0-0.9,0.1-1.3,0.4c-0.4,0.3-0.7,0.6-0.9,1.1c-0.2,0.5-0.3,1-0.3,1.7c0,0.6,0.1,1.2,0.3,1.7
	c0.2,0.5,0.5,0.8,0.9,1.1c0.4,0.3,0.8,0.4,1.3,0.4c0.4,0,0.7-0.1,1.1-0.2c0.3-0.2,0.6-0.4,0.9-0.6l0.8,1.2
	c-0.4,0.4-0.8,0.6-1.4,0.8c-0.5,0.2-1,0.3-1.5,0.3c-0.8,0-1.5-0.2-2.2-0.6c-0.6-0.4-1.1-0.9-1.5-1.6c-0.4-0.7-0.6-1.5-0.6-2.5
	c0-1,0.2-1.8,0.6-2.5c0.4-0.7,0.9-1.2,1.6-1.6C43.2,53.8,43.9,53.7,44.7,53.7z M24.1,55.2c-0.5,0-0.9,0.1-1.2,0.4
	c-0.3,0.3-0.6,0.6-0.8,1.1c-0.2,0.5-0.3,1-0.3,1.7s0.1,1.2,0.3,1.7c0.2,0.5,0.5,0.8,0.8,1.1c0.3,0.3,0.7,0.4,1.2,0.4
	c0.5,0,0.9-0.1,1.2-0.4c0.3-0.3,0.6-0.6,0.8-1.1c0.2-0.5,0.3-1,0.3-1.7c0-0.6-0.1-1.2-0.3-1.7c-0.2-0.5-0.4-0.8-0.8-1.1
	C24.9,55.3,24.5,55.2,24.1,55.2z M35.7,58.3c-0.9,0.1-1.6,0.3-2.1,0.4c-0.5,0.2-0.9,0.4-1.1,0.7c-0.2,0.3-0.3,0.6-0.3,0.9
	c0,0.4,0.1,0.8,0.4,1c0.3,0.2,0.6,0.3,1,0.3c0.4,0,0.8-0.1,1.1-0.3c0.3-0.2,0.7-0.4,1-0.8V58.3z"/>
`;
    }

    function getPattern() {
        return `
        <rect x="0" y="0" width="${mm2px(format.p.ml)}" height="100%" fill="#E0E0E0" stroke="black"/>
        <rect x="0" y="0" width="100%" height="${mm2px(format.p.mt)}" fill="#E0E0E0" stroke="black"/>
        `;
        // <rect x="0" y="0" width="50%" height="50%" fill="#E0E0E0" stroke="none"/>
        // <rect x="50%" y="50%" width="50%" height="50%" fill="#E0E0E0" stroke="none"/>
        // <rect x="50%" y="0" width="50%" height="50%" fill="#F0F0F0" stroke="none"/>
        // <rect x="0" y="50%" width="50%" height="50%" fill="#F0F0F0" stroke="none"/>
        // <rect x="0" y="0" width="100%" height="100%"/>
        // <rect x="100" y="100" width="${mm2px(20)}" height="${mm2px(20)}" fill="grey" stroke="none"/>
        // <rect x="100" y="250" width="${mm2px(30)}" height="${mm2px(30)}" fill="grey" stroke="none"/>
        // <rect x="100" y="500" width="${mm2px(50)}" height="${mm2px(50)}" fill="grey" stroke="none"/>
        // <rect x="900" y="${sizes.y - 100}" width="100" height="100"/>
    }
}
